import { createServerFn } from "@tanstack/react-start";
import type {
  ApiInterfacesHttpWorkspacesRouterOSSUploadSignatureResponse,
  WorkspaceDetailResponse,
  WorkspaceLogoUpdateRequest,
  WorkspaceTimezoneUpdateRequest,
  WorkspaceUpdateRequest,
} from "@/lib/api/generated";
import {
  getWorkspaceDetailApiWorkspacesWorkspaceIdGet,
  getWorkspaceLogoUploadSignatureApiWorkspacesWorkspaceIdLogoSignaturePost,
  updateWorkspaceApiWorkspacesWorkspaceIdPatch,
  updateWorkspaceLogoApiWorkspacesWorkspaceIdLogoPatch,
  updateWorkspaceTimezoneApiWorkspacesWorkspaceIdTimezonePatch,
} from "@/lib/api/generated/workspaces/workspaces";
import { logError, logInfo } from "@/lib/structured-log";

/**
 * 获取 workspace 详细信息
 */
export const getWorkspaceDetailServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string }) => data)
  .handler(async ({ data }): Promise<WorkspaceDetailResponse> => {
    try {
      const response = await getWorkspaceDetailApiWorkspacesWorkspaceIdGet(data.workspaceId);

      if (response.status !== 200) {
        logError("workspaces.detail.get_failed", {
          status: response.status,
          workspace_id: data.workspaceId,
        });
        throw new Error(`Failed to get workspace detail: ${response.status}`);
      }

      logInfo("workspaces.detail.get_success", { workspace_id: data.workspaceId });
      return response.data;
    } catch (error) {
      logError("workspaces.detail.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

/**
 * 更新 workspace 信息
 */
export const updateWorkspaceServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; payload: WorkspaceUpdateRequest }) => data)
  .handler(async ({ data }): Promise<WorkspaceDetailResponse> => {
    try {
      const response = await updateWorkspaceApiWorkspacesWorkspaceIdPatch(
        data.workspaceId,
        data.payload,
      );

      if (response.status !== 200) {
        logError("workspaces.update.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          display_name: data.payload.display_name,
        });
        throw new Error(`Failed to update workspace: ${response.status}`);
      }

      logInfo("workspaces.update.success", {
        workspace_id: data.workspaceId,
        display_name: data.payload.display_name,
      });
      return response.data;
    } catch (error) {
      logError("workspaces.update.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

/**
 * 获取 Workspace Logo OSS 上传签名
 */
export const getWorkspaceLogoSignatureServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; filename: string }) => data)
  .handler(
    async ({ data }): Promise<ApiInterfacesHttpWorkspacesRouterOSSUploadSignatureResponse> => {
      try {
        const response =
          await getWorkspaceLogoUploadSignatureApiWorkspacesWorkspaceIdLogoSignaturePost(
            data.workspaceId,
            { filename: data.filename },
          );

        if (response.status !== 200) {
          logError("workspaces.logo.signature.failed", {
            status: response.status,
            workspace_id: data.workspaceId,
            filename: data.filename,
          });
          throw new Error(`Failed to get logo upload signature: ${response.status}`);
        }

        logInfo("workspaces.logo.signature.success", {
          workspace_id: data.workspaceId,
          filename: data.filename,
        });
        return response.data;
      } catch (error) {
        logError("workspaces.logo.signature.error", {
          error: error instanceof Error ? error.message : "unknown-error",
          workspace_id: data.workspaceId,
        });
        throw error;
      }
    },
  );

/**
 * 更新 Workspace Logo
 */
export const updateWorkspaceLogoServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; payload: WorkspaceLogoUpdateRequest }) => data)
  .handler(async ({ data }): Promise<WorkspaceDetailResponse> => {
    try {
      const response = await updateWorkspaceLogoApiWorkspacesWorkspaceIdLogoPatch(
        data.workspaceId,
        data.payload,
      );

      if (response.status !== 200) {
        logError("workspaces.logo.update.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          logo_oss_key: data.payload.logo_oss_key,
        });
        throw new Error(`Failed to update workspace logo: ${response.status}`);
      }

      logInfo("workspaces.logo.update.success", {
        workspace_id: data.workspaceId,
        logo_oss_key: data.payload.logo_oss_key,
      });
      return response.data;
    } catch (error) {
      logError("workspaces.logo.update.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

/**
 * 更新 Workspace 时区
 */
export const updateWorkspaceTimezoneServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; timezone: string }) => data)
  .handler(async ({ data }): Promise<WorkspaceDetailResponse> => {
    try {
      const payload: WorkspaceTimezoneUpdateRequest = {
        timezone: data.timezone,
      };

      const response = await updateWorkspaceTimezoneApiWorkspacesWorkspaceIdTimezonePatch(
        data.workspaceId,
        payload,
      );

      if (response.status !== 200) {
        logError("workspaces.timezone.update.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          timezone: data.timezone,
        });
        throw new Error(`Failed to update workspace timezone: ${response.status}`);
      }

      logInfo("workspaces.timezone.update.success", {
        workspace_id: data.workspaceId,
        timezone: data.timezone,
      });
      return response.data;
    } catch (error) {
      logError("workspaces.timezone.update.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });
